/**
 * PropertyValuesTable - Notion-style property values table
 *
 * Displays between the document header and the editor.
 * Shows document properties as rows with inline editors for each property type.
 * Saves on blur/change via the properties API.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Type,
  Hash,
  List,
  CheckSquare,
  Calendar,
  Link2,
  Mail,
  Phone,
  ChevronDown,
  X,
  Loader2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { propertiesApi } from '../../api/properties';
import type { DocumentProperty, PropertyType, PropertyOption } from '../../api/properties';

// ============================================================================
// Props
// ============================================================================

interface PropertyValuesTableProps {
  documentId: string;
  projectId: string;
  onPropertiesLoaded?: (count: number) => void;
}

// ============================================================================
// Type icon mapping
// ============================================================================

const TYPE_ICONS: Record<string, LucideIcon> = {
  rich_text: Type,
  number: Hash,
  select: List,
  multi_select: List,
  checkbox: CheckSquare,
  date: Calendar,
  url: Link2,
  email: Mail,
  phone: Phone,
  status: List,
};

// ============================================================================
// Color utilities for select/status badges
// ============================================================================

const BADGE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  default: { bg: 'bg-neutral-100', text: 'text-neutral-700', border: 'border-neutral-200' },
  gray: { bg: 'bg-neutral-100', text: 'text-neutral-700', border: 'border-neutral-200' },
  brown: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  green: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  pink: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  coral: { bg: 'bg-coral-50', text: 'text-coral-700', border: 'border-coral-200' },
};

function getBadgeClasses(color: string | null): string {
  const c = BADGE_COLORS[color || 'default'] || BADGE_COLORS.default;
  return `${c.bg} ${c.text} ${c.border}`;
}

// Status group colors
const STATUS_GROUP_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  not_started: { bg: 'bg-neutral-100', text: 'text-neutral-600', dot: 'bg-neutral-400' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  complete: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

function getStatusGroupClasses(group: string | null): { bg: string; text: string; dot: string } {
  return STATUS_GROUP_COLORS[group || 'not_started'] || STATUS_GROUP_COLORS.not_started;
}

// ============================================================================
// Inline Editors
// ============================================================================

/** Text input for rich_text, url, email, phone */
function TextEditor({
  value,
  onChange,
  placeholder,
  type = 'text',
  icon: Icon,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  type?: string;
  icon?: LucideIcon;
}) {
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleBlur = useCallback(() => {
    if (local !== value) {
      onChange(local);
    }
  }, [local, value, onChange]);

  return (
    <div className="flex items-center gap-1.5 w-full">
      {Icon && <Icon className="w-3.5 h-3.5 text-neutral-400 shrink-0" />}
      <input
        ref={inputRef}
        type={type}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            inputRef.current?.blur();
          }
        }}
        placeholder={placeholder || 'Empty'}
        className="w-full bg-transparent text-sm text-neutral-800 placeholder:text-neutral-300
          outline-none border-none py-0.5 px-1 rounded
          hover:bg-neutral-50 focus:bg-white focus:ring-1 focus:ring-coral-300
          transition-colors"
      />
    </div>
  );
}

/** Number input */
function NumberEditor({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (val: number | null) => void;
}) {
  const [local, setLocal] = useState(value !== null && value !== undefined ? String(value) : '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocal(value !== null && value !== undefined ? String(value) : '');
  }, [value]);

  const handleBlur = useCallback(() => {
    const parsed = local === '' ? null : Number(local);
    if (parsed !== value) {
      onChange(parsed);
    }
  }, [local, value, onChange]);

  return (
    <input
      ref={inputRef}
      type="number"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          inputRef.current?.blur();
        }
      }}
      placeholder="Empty"
      className="w-full bg-transparent text-sm text-neutral-800 placeholder:text-neutral-300
        outline-none border-none py-0.5 px-1 rounded
        hover:bg-neutral-50 focus:bg-white focus:ring-1 focus:ring-coral-300
        transition-colors [appearance:textfield]
        [&::-webkit-outer-spin-button]:appearance-none
        [&::-webkit-inner-spin-button]:appearance-none"
    />
  );
}

/** Checkbox toggle */
function CheckboxEditor({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`
        relative w-8 h-[18px] rounded-full transition-colors shrink-0
        ${value ? 'bg-coral-500' : 'bg-neutral-300'}
      `}
      aria-label={value ? 'Checked' : 'Unchecked'}
    >
      <span
        className={`
          absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow-sm
          transition-transform
          ${value ? 'translate-x-[14px]' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

/** Date input */
function DateEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (val: string | null) => void;
}) {
  const [local, setLocal] = useState(value || '');

  useEffect(() => {
    setLocal(value || '');
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVal = e.target.value;
      setLocal(newVal);
      onChange(newVal || null);
    },
    [onChange]
  );

  return (
    <input
      type="date"
      value={local}
      onChange={handleChange}
      className="bg-transparent text-sm text-neutral-800 placeholder:text-neutral-300
        outline-none border-none py-0.5 px-1 rounded
        hover:bg-neutral-50 focus:bg-white focus:ring-1 focus:ring-coral-300
        transition-colors"
    />
  );
}

/** Single-select dropdown */
function SelectEditor({
  value,
  options,
  onChange,
  isStatus,
}: {
  value: string | null;
  options: PropertyOption[];
  onChange: (val: string | null) => void;
  isStatus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find((o) => o.id === value || o.name === value);

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 w-full py-0.5 px-1 rounded
          hover:bg-neutral-50 transition-colors text-left"
      >
        {selected ? (
          isStatus ? (
            <StatusBadge option={selected} />
          ) : (
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${getBadgeClasses(selected.color)}`}
            >
              {selected.name}
            </span>
          )
        ) : (
          <span className="text-sm text-neutral-300">Empty</span>
        )}
        <ChevronDown className="w-3 h-3 text-neutral-400 ml-auto shrink-0" />
      </button>

      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg
            border border-neutral-200 py-1 max-h-60 overflow-y-auto"
        >
          {/* Clear option */}
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="w-full text-left px-3 py-1.5 text-xs text-neutral-400 hover:bg-neutral-50"
          >
            Clear
          </button>
          {options
            .filter((o) => !o.is_archived)
            .map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.name);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center gap-2
                  ${(selected?.id === option.id || selected?.name === option.name) ? 'bg-neutral-50' : ''}`}
              >
                {isStatus ? (
                  <StatusBadge option={option} />
                ) : (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${getBadgeClasses(option.color)}`}
                  >
                    {option.name}
                  </span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/** Status badge with dot indicator */
function StatusBadge({ option }: { option: PropertyOption }) {
  const colors = getStatusGroupClasses(option.status_group);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded ${colors.bg} ${colors.text}`}
    >
      <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
      {option.name}
    </span>
  );
}

/** Multi-select dropdown with tags */
function MultiSelectEditor({
  value,
  options,
  onChange,
}: {
  value: string[];
  options: PropertyOption[];
  onChange: (val: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedOptions = options.filter((o) => value.includes(o.id) || value.includes(o.name));

  const toggleOption = useCallback(
    (optionName: string) => {
      const next = value.includes(optionName)
        ? value.filter((v) => v !== optionName)
        : [...value, optionName];
      onChange(next);
    },
    [value, onChange]
  );

  const removeTag = useCallback(
    (optionName: string) => {
      onChange(value.filter((v) => v !== optionName));
    },
    [value, onChange]
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full py-0.5 px-1 rounded
          hover:bg-neutral-50 transition-colors text-left flex-wrap min-h-[28px]"
      >
        {selectedOptions.length > 0 ? (
          selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded border ${getBadgeClasses(opt.color)}`}
            >
              {opt.name}
              <X
                className="w-3 h-3 cursor-pointer opacity-60 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(opt.name);
                }}
              />
            </span>
          ))
        ) : (
          <span className="text-sm text-neutral-300">Empty</span>
        )}
        <ChevronDown className="w-3 h-3 text-neutral-400 ml-auto shrink-0" />
      </button>

      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg
            border border-neutral-200 py-1 max-h-60 overflow-y-auto"
        >
          {options
            .filter((o) => !o.is_archived)
            .map((option) => {
              const isSelected = value.includes(option.id) || value.includes(option.name);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => toggleOption(option.name)}
                  className={`w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center gap-2
                    ${isSelected ? 'bg-neutral-50' : ''}`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0
                      ${isSelected ? 'bg-coral-500 border-coral-500' : 'border-neutral-300'}`}
                  >
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${getBadgeClasses(option.color)}`}
                  >
                    {option.name}
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Property Row
// ============================================================================

function PropertyRow({
  property,
  onSave,
}: {
  property: DocumentProperty;
  onSave: (propertyId: string, value: unknown) => void;
}) {
  const Icon = TYPE_ICONS[property.property_type] || Type;
  const options = property.options || [];

  const handleChange = useCallback(
    (val: unknown) => {
      onSave(property.property_id, val);
    },
    [property.property_id, onSave]
  );

  function renderEditor() {
    switch (property.property_type) {
      case 'rich_text':
        return (
          <TextEditor
            value={(property.value as string) || ''}
            onChange={handleChange}
          />
        );

      case 'number':
        return (
          <NumberEditor
            value={property.value as number | null}
            onChange={handleChange}
          />
        );

      case 'select':
        return (
          <SelectEditor
            value={(property.value as string) || null}
            options={options}
            onChange={handleChange}
          />
        );

      case 'multi_select':
        return (
          <MultiSelectEditor
            value={Array.isArray(property.value) ? property.value : []}
            options={options}
            onChange={handleChange}
          />
        );

      case 'checkbox':
        return (
          <CheckboxEditor
            value={Boolean(property.value)}
            onChange={handleChange}
          />
        );

      case 'date':
        return (
          <DateEditor
            value={(property.value as string) || ''}
            onChange={handleChange}
          />
        );

      case 'url':
        return (
          <TextEditor
            value={(property.value as string) || ''}
            onChange={handleChange}
            placeholder="https://..."
            type="url"
            icon={Link2}
          />
        );

      case 'email':
        return (
          <TextEditor
            value={(property.value as string) || ''}
            onChange={handleChange}
            placeholder="email@example.com"
            type="email"
            icon={Mail}
          />
        );

      case 'phone':
        return (
          <TextEditor
            value={(property.value as string) || ''}
            onChange={handleChange}
            placeholder="+1 (555) 000-0000"
            type="tel"
            icon={Phone}
          />
        );

      case 'status':
        return (
          <SelectEditor
            value={(property.value as string) || null}
            options={options}
            onChange={handleChange}
            isStatus
          />
        );

      default:
        return (
          <span className="text-sm text-neutral-300 px-1">Unsupported type</span>
        );
    }
  }

  return (
    <div className="flex items-center h-9 border-b border-neutral-100 group hover:bg-neutral-50/50 transition-colors">
      {/* Property name + type icon */}
      <div className="flex items-center gap-2 w-44 shrink-0 px-3">
        <Icon className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
        <span className="text-xs font-medium text-neutral-500 truncate">
          {property.property_name}
        </span>
        {property.is_required && (
          <span className="text-coral-500 text-xs" title="Required">*</span>
        )}
      </div>

      {/* Value editor */}
      <div className="flex-1 min-w-0 px-2">
        {renderEditor()}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function PropertyValuesTable({ documentId, projectId, onPropertiesLoaded }: PropertyValuesTableProps) {
  const [properties, setProperties] = useState<DocumentProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  // Fetch document properties
  useEffect(() => {
    let cancelled = false;

    async function fetchProperties() {
      if (!documentId || !projectId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await propertiesApi.getDocumentProperties(projectId, documentId);
        if (!cancelled) {
          setProperties(data);
          onPropertiesLoaded?.(data.length);
        }
      } catch (err) {
        if (!cancelled) {
          // If 404 or no properties defined, just show nothing
          setProperties([]);
          onPropertiesLoaded?.(0);
          if (err instanceof Error && !err.message.includes('404')) {
            setError(err.message);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchProperties();
    return () => {
      cancelled = true;
    };
  }, [documentId, projectId]);

  // Save a property value
  const handleSave = useCallback(
    async (propertyId: string, value: unknown) => {
      setSavingIds((prev) => new Set(prev).add(propertyId));

      try {
        await propertiesApi.setPropertyValue(projectId, documentId, propertyId, value);

        // Update local state optimistically
        setProperties((prev) =>
          prev.map((p) =>
            p.property_id === propertyId ? { ...p, value } : p
          )
        );
      } catch (err) {
        console.error('Failed to save property value:', err);
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(propertyId);
          return next;
        });
      }
    },
    [projectId, documentId]
  );

  // Don't render if loading, errored, or no properties
  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (properties.length === 0) {
    return null;
  }

  const sorted = [...properties].sort((a, b) => a.order - b.order);

  return (
    <div className="border border-neutral-200 rounded-lg bg-white mb-4 overflow-hidden">
      {/* Saving indicator */}
      {savingIds.size > 0 && (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-coral-50 text-coral-600 text-xs border-b border-coral-100">
          <Loader2 className="w-3 h-3 animate-spin" />
          Saving...
        </div>
      )}

      {sorted.map((property) => (
        <PropertyRow
          key={property.property_id}
          property={property}
          onSave={handleSave}
        />
      ))}
    </div>
  );
}
